import { useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { PageBody, Spinner, Tab, TabList } from '@wundo/ui';
import type {
  MeetingTemplateKind,
  MeetingTemplateRecord,
} from '@wundo/shared/meetingTemplate.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import {
  listDefaults,
  listForOrganisation,
  makeDefaultForOrganisation,
  reorderDefaults,
  reorderForOrganisation,
} from '@wundo/client/staffMeetingTemplates.ts';
import { MeetingTemplateKindLists } from '@/components/meeting-templates/MeetingTemplateKindLists.tsx';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { ReadingColumn } from '@/components/ReadingColumn.tsx';
import { reorderById } from '@/utils/reorderById.ts';

/** Which collection a write goes to. */
type Scope = 'defaults' | 'organisation';

/*
 * The form lives on its own page, so returning from it has to put the list back
 * on the tab it was left on. `defaults` is the tab nobody has to ask for, so it
 * never appears in the URL.
 */
export interface MeetingTemplatesSearch {
  tab?: 'organisation';
}

function validateSearch(search: Record<string, unknown>): MeetingTemplatesSearch {
  return {
    tab: search.tab === 'organisation' ? search.tab : undefined,
  };
}

export const Route = createFileRoute('/_app/meeting-templates/')({
  staticData: { orgScope: 'required' },
  validateSearch,
  component: MeetingTemplatesRoute,
});

function MeetingTemplatesRoute() {
  const router = useRouter();
  const search = Route.useSearch();

  return (
    <MeetingTemplatesPage
      search={search}
      onSearchChange={(next) =>
        void router.navigate({ to: '/meeting-templates', search: next, replace: true })
      }
      onOpen={(path) => void router.navigate({ href: path })}
    />
  );
}

const TABS: { key: Scope; label: string }[] = [
  { key: 'defaults', label: 'Wundo defaults' },
  { key: 'organisation', label: 'Organisation' },
];

export interface MeetingTemplatesPageProps {
  search: MeetingTemplatesSearch;
  onSearchChange: (search: MeetingTemplatesSearch) => void;
  onOpen: (path: string) => void;
}

/**
 * Conversation types as Wundo staff keep them: the defaults every organisation
 * is handed, and one organisation's own list. An organisation owns its copies
 * from the moment it exists, so editing a default never reaches into its list.
 */
export function MeetingTemplatesPage({
  search,
  onSearchChange,
  onOpen,
}: MeetingTemplatesPageProps) {
  const queryClient = useQueryClient();

  const activeTab: Scope = search.tab ?? 'defaults';
  const { organisation } = useActiveOrganisation();
  const organisationId = organisation?.id ?? null;

  const defaults = useQuery({
    queryKey: ['meetingTemplateDefaults'],
    queryFn: listDefaults,
  });
  const organisationTemplates = useQuery({
    queryKey: ['meetingTemplates', organisationId],
    queryFn: () => listForOrganisation(organisationId!),
    enabled: organisationId !== null,
  });

  const [defaultsActionError, setDefaultsActionError] = useState('');
  const [organisationActionError, setOrganisationActionError] = useState('');

  const moveDefaults = useOptimisticReorder(
    ['meetingTemplateDefaults'],
    reorderDefaults,
    setDefaultsActionError,
  );
  const moveOrganisation = useOptimisticReorder(
    ['meetingTemplates', organisationId],
    (ids) => reorderForOrganisation(organisationId!, ids),
    setOrganisationActionError,
  );

  /** Only an organisation's own list carries the marks its meetings follow, one per kind. */
  const makeDefault = useMutation({
    mutationFn: (templateId: string) =>
      makeDefaultForOrganisation(organisationId!, templateId),
    onMutate: () => setOrganisationActionError(''),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['meetingTemplates', organisationId] }),
    onError: (failure) =>
      setOrganisationActionError(
        apiErrorMessage(failure, 'Failed to make this the default'),
      ),
  });

  /** Both scopes edit on the same page; the route says which list it belongs to. */
  function formPath(scope: Scope, templateId: string): string {
    if (scope === 'defaults') return `/meeting-templates/defaults/${templateId}`;
    return `/meeting-templates/organisations/${organisationId}/${templateId}`;
  }

  /* A new type starts as the kind of the card it was asked for from. */
  function newPath(scope: Scope, kind: MeetingTemplateKind): string {
    return `${formPath(scope, 'new')}?kind=${kind}`;
  }

  function renderDefaults() {
    if (defaults.isPending) {
      return <Spinner />;
    }
    if (defaults.isError) {
      return (
        <p role='alert' className='text-sm text-danger'>
          Failed to load the default conversation types
        </p>
      );
    }
    return (
      <div className='flex flex-col gap-4'>
        <p className='text-sm text-ink-muted'>
          Every organisation is handed a copy of each of these when it is created.
        </p>
        <MeetingTemplateKindLists
          templates={defaults.data}
          error={defaultsActionError || undefined}
          onOpen={(templateId) => onOpen(formPath('defaults', templateId))}
          onCreate={(kind) => onOpen(newPath('defaults', kind))}
          onReorder={moveDefaults}
        />
      </div>
    );
  }

  function renderOrganisationTemplates() {
    if (organisationTemplates.isPending) {
      return <Spinner />;
    }
    if (organisationTemplates.isError) {
      return (
        <p role='alert' className='text-sm text-danger'>
          Failed to load this organisation&rsquo;s conversation types
        </p>
      );
    }
    return (
      <MeetingTemplateKindLists
        templates={organisationTemplates.data}
        error={organisationActionError || undefined}
        onOpen={(templateId) => onOpen(formPath('organisation', templateId))}
        onCreate={(kind) => onOpen(newPath('organisation', kind))}
        onReorder={moveOrganisation}
        onMakeDefault={(templateId) => makeDefault.mutate(templateId)}
      />
    );
  }

  return (
    <>
      <PageHeader title='Conversation types' />
      <PageBody className='mt-3.5'>
        <div className='flex flex-col gap-5'>
          {/* A list page across the width, but its intro still a line you can read. */}
          <ReadingColumn>
            <p className='text-sm text-ink-muted'>
              The structure preparations and reports follow. Editing a default changes
              what the next organisation starts with — organisations keep the copies they
              already have.
            </p>
          </ReadingColumn>

          <TabList>
            {TABS.map((tab) => (
              <Tab
                key={tab.key}
                active={tab.key === activeTab}
                onClick={() =>
                  onSearchChange({ tab: tab.key === 'defaults' ? undefined : tab.key })
                }
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>

          {activeTab === 'defaults' && renderDefaults()}

          {activeTab === 'organisation' && (
            <div className='flex flex-col gap-4'>
              <p className='text-sm text-ink-muted'>
                {organisation?.name ?? 'This organisation'}&rsquo;s conversation types.
                Personal conversation types are not shown here — those belong to the
                person who wrote them.
              </p>

              {organisationId !== null && renderOrganisationTemplates()}
            </div>
          )}
        </div>
      </PageBody>
    </>
  );
}

/**
 * A reorder shows at once and is confirmed after: the rows move locally, the
 * whole arrangement goes up, and the list is read back either way, so the
 * server has the last word on what the order is.
 */
function useOptimisticReorder(
  queryKey: QueryKey,
  reorder: (ids: string[]) => Promise<unknown>,
  setError: (message: string) => void,
): (ids: string[]) => void {
  const queryClient = useQueryClient();

  const move = useMutation({
    mutationFn: reorder,
    onMutate: (ids) => {
      setError('');
      queryClient.setQueryData<MeetingTemplateRecord[]>(queryKey, (current) =>
        current ? reorderById(current, ids) : current,
      );
    },
    onError: (failure) =>
      setError(apiErrorMessage(failure, 'Failed to save the new order')),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  return (ids) => move.mutate(ids);
}
